import { useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dateKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

// Owner-side month calendar built from the bookings the inbox already has
// loaded — green = booked (accepted), amber = requested (pending). Refresh,
// accept, deny or delete in the inbox and it updates instantly.
export default function InboxCalendar({ bookings }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });

  const moveMonth = (dir) =>
    setCursor(({ y, m }) => {
      const nm = m + dir;
      return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });

  const byDate = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      if (!b.game_date || b.status === "denied" || b.status === "cancelled") return;
      const entry = (map[b.game_date] = map[b.game_date] || { booked: [], requested: [] });
      if (b.status === "accepted") entry.booked.push(b);
      else entry.requested.push(b);
    });
    return map;
  }, [bookings]);

  const today = new Date().toLocaleDateString("en-CA");

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1).getDay();
    const total = new Date(cursor.y, cursor.m + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: total }, (_, i) => i + 1)];
  }, [cursor]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  const dayTitle = (info, isPast) => {
    if (!info) return isPast ? undefined : "Open";
    const parts = [];
    info.booked.forEach((b) => parts.push(`Booked: ${b.name}${b.team ? ` — ${b.team}` : ""}`));
    info.requested.forEach((b) => parts.push(`Requested: ${b.name}${b.team ? ` — ${b.team}` : ""}`));
    return parts.join("\n");
  };

  const dayClass = (info, isPast, isToday) => {
    if (info?.booked.length)
      return "bg-gradient-to-br from-emerald-500/25 to-emerald-500/10 border border-emerald-400/40 text-white font-bold";
    if (info?.requested.length)
      return "bg-gradient-to-br from-amber-400/25 to-amber-400/10 border border-amber-300/50 text-white font-bold";
    if (isPast) return "border border-transparent text-white/20";
    if (isToday) return "border border-accentlight/60 text-accentlight";
    return "border border-white/10 text-white/70";
  };

  const jumpToBooking = (info) => {
    const first = info?.booked?.[0] || info?.requested?.[0];
    if (!first) return;
    const row = document.querySelector(`[data-testid="inbox-row-${first.id}"]`);
    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="mb-10 rounded-2xl border border-white/5 bg-surface p-6 md:p-8" data-testid="inbox-calendar">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2
          className="flex items-center gap-2 font-display text-2xl tracking-wider"
          data-testid="inbox-calendar-title"
        >
          <CalendarDays className="h-5 w-5 text-accentlight" />
          BOOKING <span className="text-accentlight">CALENDAR</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => moveMonth(-1)}
            data-testid="inbox-calendar-prev"
            aria-label="Previous month"
            className="rounded-lg border border-white/10 p-2 text-white/60 transition-colors hover:border-accentlight hover:text-accentlight"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => moveMonth(1)}
            data-testid="inbox-calendar-next"
            aria-label="Next month"
            className="rounded-lg border border-white/10 p-2 text-white/60 transition-colors hover:border-accentlight hover:text-accentlight"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <p className="mb-4 font-display text-3xl tracking-wider" data-testid="inbox-calendar-month-label">
        {monthLabel}
      </p>

      <div className="mb-2 grid grid-cols-7 gap-1.5">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-1 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`empty-${i}`} />;
          const key = dateKey(cursor.y, cursor.m, d);
          const info = byDate[key];
          const isPast = key < today;
          const isToday = key === today;
          const marked = Boolean(info?.booked.length || info?.requested.length);
          return (
            <button
              key={key}
              type="button"
              data-testid={`inbox-calendar-day-${key}`}
              title={dayTitle(info, isPast)}
              onClick={() => jumpToBooking(info)}
              className={`relative flex aspect-square items-center justify-center rounded-lg text-sm transition-transform duration-200 ${dayClass(info, isPast, isToday)} ${marked ? "cursor-pointer hover:scale-105" : "cursor-default"} ${isToday && !info ? "ring-1 ring-accentlight" : ""}`}
            >
              {d}
            </button>
          );
        })}
      </div>

      <div
        className="mt-5 flex flex-wrap items-center gap-5 text-xs font-semibold uppercase tracking-[0.2em] text-white/50"
        data-testid="inbox-calendar-legend"
      >
        <span className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500/25 border border-emerald-400/40" />
          Booked
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm bg-amber-400/25 border border-amber-300/50" />
          Requested
        </span>
        <span className="flex items-center gap-2 normal-case tracking-normal text-white/30">
          Tap a marked date to jump to that booking
        </span>
      </div>
    </div>
  );
}
